import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import { queries } from "@/api";
import { PcSettings } from "@/components/PcSettings";
import { useHost } from "@/host/host";
import { hostQueries } from "@/host/queries";
import { TEXT_SIZES, applyTextSize, readTextSize, type TextSize } from "@/lib/textSize";
import { THEMES, applyTheme, resolveTheme, useTheme, type Theme } from "@/lib/theme";

/** Settings (decision 071, F441; in named sections since F499, decision 105; the one Settings page since decision 141): how the pages read, the companion on this PC with its settings, the account and the privacy. */
export const Route = createFileRoute("/settings")({
  component: Settings,
});

function Settings() {
  const host = useHost();
  const info = useQuery({ ...hostQueries.info(host!), enabled: !!host });
  const me = useQuery(queries.me());
  const [size, setSize] = useState<TextSize>(() => readTextSize());
  const choose = (next: TextSize) => {
    setSize(next);
    applyTextSize(next);
  };
  // The appearance (F467, decision 090): the page at once, the shell's window with it.
  const theme = useTheme();
  const chooseTheme = (next: Theme) => {
    applyTheme(next);
    host?.setWindowTheme(next === "system" ? null : resolveTheme(next)).catch(() => undefined);
  };
  return (
    <div className="space-y-6" data-testid="settings-page">
      <header>
        <h1 className="page-title">Settings</h1>
        <p className="muted text-sm" data-testid="settings-lead">
          {host
            ? "How the pages read, the companion on this PC, and who you are to the others. Save keeps the folder, the liveries and the updates; the start-up applies at once."
            : me.data
              ? "How the pages read, and who you are to the others."
              : "How the pages read. Sign in for the rest."}
        </p>
      </header>

      <section className="space-y-3" aria-labelledby="st-reading">
        <div>
          <h2 id="st-reading" className="text-[1.0714rem] font-semibold">
            Reading
          </h2>
          <p className="muted text-xs">Kept on this device alone.</p>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <section className="card">
            <h3 className="font-semibold">Text size</h3>
            <p className="muted text-xs">Everything scales with it, the tables and the cockpit facts too.</p>
            <div className="mt-3 flex flex-wrap gap-2" role="radiogroup" aria-label="Text size">
              {TEXT_SIZES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  role="radio"
                  aria-checked={size === t.value}
                  className={`btn ${size === t.value ? "btn-primary" : ""}`}
                  onClick={() => choose(t.value)}
                  data-testid={`text-size-${t.value}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </section>
          <section className="card" data-testid="settings-appearance">
            <h3 className="font-semibold">Appearance</h3>
            <p className="muted text-xs">{host ? "The companion's window follows it." : "The maps follow it too."}</p>
            <div className="mt-3 flex flex-wrap gap-2" role="radiogroup" aria-label="Appearance">
              {THEMES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  role="radio"
                  aria-checked={theme === t.value}
                  title={t.hint}
                  className={`btn ${theme === t.value ? "btn-primary" : ""}`}
                  onClick={() => chooseTheme(t.value)}
                  data-testid={`theme-${t.value}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </section>
        </div>
      </section>

      {host ? (
        <section className="space-y-3" aria-labelledby="st-pc" data-testid="settings-pc" id="pc">
          <div>
            <h2 id="st-pc" className="text-[1.0714rem] font-semibold">
              This PC
            </h2>
            <p className="muted text-xs">
              The companion on this PC: where the airframes are, what a booking brings, how it updates and starts.
              {info.data ? (
                <span data-testid="settings-versions">
                  {" "}
                  Companion {info.data.version}
                  {info.data.plugin_installed ? `, the plugin in X-Plane${info.data.plugin_ready ? ` (${info.data.plugin_ready} fetched)` : ""}` : ", no plugin found in X-Plane"}.
                </span>
              ) : null}
            </p>
          </div>
          <PcSettings />
        </section>
      ) : null}

      {me.data ? (
        <section className="space-y-3" aria-labelledby="st-you" data-testid="settings-account-section">
          <div>
            <h2 id="st-you" className="text-[1.0714rem] font-semibold">
              You
            </h2>
            <p className="muted text-xs">What the other pilots see of you, your profile, your Discord, and leaving the platform.</p>
          </div>
          <ul className="card divide-y divide-line !p-0">
            <PcLink to="/account" testId="settings-account" title="Account & privacy" text="Your first and last name, your handle, what the other pilots see of you, the e-mails you receive, leaving the platform." />
            <PcLink to="/privacy" testId="settings-data" title="How your data is kept" text="What the platform keeps of you and of the aircraft, who sees it, how to take it away." />
          </ul>
        </section>
      ) : null}
    </div>
  );
}

function PcLink({ to, hash, testId, title, text }: { to: "/account" | "/privacy"; hash?: string; testId: string; title: string; text: string }) {
  return (
    <li>
      <Link to={to} hash={hash} className="flex items-center gap-3 px-5 py-3 hover:bg-paper-2" data-testid={testId}>
        <span className="min-w-0 flex-1">
          <span className="font-semibold">{title}</span>
          <span className="muted block text-xs">{text}</span>
        </span>
        <span className="muted text-xs">Open</span>
      </Link>
    </li>
  );
}
